import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { IconEye, IconTrash, IconSearch, IconLogout } from "@tabler/icons-react";
import clsx from "clsx";
import { KeyConstant, LangConstant, PathConstant, SystemConstant } from "const";
import { AuthUtils, BranchUtils, StorageUtils } from "utils";
import { useBranchList, useDeleteBranch, useUpdateBranch } from "hooks";
import { CreateBranch, PopupConfirm } from "./components";

const BranchManagement = () => {
  const { t: getLabel } = useTranslation(LangConstant.NS_COMMON);
  const history = useHistory();

  const role = Number(StorageUtils.getStoreData(KeyConstant.KEY_ADMIN_ROLE));

  const {
    branchList,
    pagination,
    searchInput,
    handlePageChange,
    handleChangeRowPerPage,
    handleChangeSearchInput,
    handleLabelDisplayedRows,
  } = useBranchList();

  const { changeBranchStatus } = useUpdateBranch();
  const { deleteBranch } = useDeleteBranch();

  const sidebarTitles = getLabel(LangConstant.OBJ_SIDEBAR_TITLE, { returnObjects: true });
  const tableBranchObj = getLabel(LangConstant.OBJ_TABLE_BRANCH, { returnObjects: true });
  const tableErrorObj = getLabel(LangConstant.OBJ_TABLE_ERROR, { returnObjects: true });

  const tableField = [
    { label: tableBranchObj.number },
    { label: tableBranchObj.avatar },
    { label: tableBranchObj.branchName },
    { label: tableBranchObj.package },
    { label: tableBranchObj.packageId },
    { label: tableBranchObj.license },
    { label: tableBranchObj.existAccount },
    { label: tableBranchObj.status },
    { label: tableBranchObj.action },
  ];

  const [isOpenCreate, setIsOpenCreate] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const handleOpenCreateBranchDialog = () => {
    setIsOpenCreate(true);
  };

  const handleCloseCreateBranchDialog = () => {
    setIsOpenCreate(false);
  };

  const handleViewBranchDetails = branch => {
    BranchUtils.handlePersistBranch(branch);
    history.push(PathConstant.STATISTIC);
  };

  const handleOpenConfirmDeleteBranchDialog = branch => {
    setSelectedBranch(branch);
  };

  const handleCloseConfirmDeleteBranchDialog = () => {
    setSelectedBranch(null);
  };

  const handleDeleteBranch = () => {
    deleteBranch({
      state: SystemConstant.ACTIVE_STATUS.inactive,
      token: selectedBranch.accessToken,
      id: selectedBranch.id,
    });
  };

  const handleChangeBranchState = (event, branch) => {
    changeBranchStatus({
      ...branch,
      state: event.target.checked
        ? SystemConstant.BRANCH_ACTIVE_STATUS.active
        : SystemConstant.BRANCH_ACTIVE_STATUS.inactive,
    });
  };

  const totalPages = Math.ceil(pagination.count / pagination.rowPerPage);

  return (
    <div style={{ padding: 50, minHeight: "100vh" }}>
      <div className="d-flex justify-content-between">
        <h5 style={{ marginBottom: 40 }}>{sidebarTitles.branchManagement}</h5>
        <div className="d-flex align-items-center" style={{ paddingBottom: 25 }}>
          <span style={{ marginRight: 10, fontSize: 16 }}>
            {StorageUtils.getStoreData(KeyConstant.KEY_USERNAME)}
          </span>
          <button className="btn btn-icon btn-ghost-secondary btn-sm" onClick={AuthUtils.handleLogout}>
            <IconLogout size={18} />
          </button>
        </div>
      </div>
      <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 30 }}>
        <button className="btn btn-primary" style={{ width: 150 }} onClick={handleOpenCreateBranchDialog}>
          {getLabel(LangConstant.L_CREATE)}
        </button>
        <div className="input-group" style={{ maxWidth: "30%" }}>
          <span className="input-group-text">
            <IconSearch size={18} />
          </span>
          <input
            type="text"
            className="form-control"
            value={searchInput}
            onChange={handleChangeSearchInput}
            placeholder={getLabel(LangConstant.P_SEARCH)}
          />
        </div>
      </div>
      <div className="card">
        <div className="table-responsive">
          <table className="table table-vcenter">
            <thead>
              <tr>
                {tableField.map(item => (
                  <th
                    key={item.label}
                    className="text-center"
                    style={{ textTransform: "uppercase", fontSize: 14, fontWeight: "bold" }}
                  >
                    {item.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {branchList.map((item, index) => (
                <tr key={index}>
                  <td className="text-center" style={{ fontSize: 14 }}>
                    <strong>{index + 1}</strong>
                  </td>
                  <td className="text-center" style={{ fontSize: 14 }}>
                    <div className="d-flex align-items-center justify-content-center w-100">
                      {item.avatar ? (
                        <img
                          src={item.avatar}
                          alt={item?.name?.charAt(0)}
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            border: "1px solid rgba(0,0,0,0.25)",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            border: "1px solid rgba(0,0,0,0.25)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#e0e0e0",
                          }}
                        >
                          {item?.name?.charAt(0)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td
                    className="text-center"
                    style={{ fontSize: 14, color: "blue", cursor: "pointer" }}
                    onClick={() => handleViewBranchDetails(item)}
                  >
                    {item.name}
                  </td>
                  <td className="text-center" style={{ fontSize: 14 }}>
                    {item.package}
                  </td>
                  <td className="text-center" style={{ fontSize: 14 }}>
                    {item.packageId}
                  </td>
                  <td className="text-center" style={{ fontSize: 14 }}>
                    {item.license}
                  </td>
                  <td className="text-center" style={{ fontSize: 14 }}>
                    {item.existedAccount}
                  </td>
                  <td className="text-center" style={{ fontSize: 14 }}>
                    <label className="form-check form-switch d-inline-block mb-0">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={Boolean(item.state)}
                        onChange={event => handleChangeBranchState(event, item)}
                      />
                    </label>
                  </td>
                  <td className="text-center" style={{ fontSize: 14 }}>
                    <button
                      className="btn btn-icon btn-ghost-primary btn-sm"
                      onClick={() => handleViewBranchDetails(item)}
                    >
                      <IconEye size={18} />
                    </button>
                    <button
                      className="btn btn-icon btn-ghost-danger btn-sm"
                      onClick={() => handleOpenConfirmDeleteBranchDialog(item)}
                    >
                      <IconTrash size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <hr className="m-0" />
        <div className="card-footer d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2" style={{ fontSize: 14 }}>
            <span>{tableErrorObj.rowsPerPage}</span>
            <select
              className="form-select form-select-sm"
              style={{ width: "auto" }}
              value={pagination.rowPerPage}
              onChange={handleChangeRowPerPage}
            >
              {pagination.rowPerPageOptions.map(opt => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span style={{ fontSize: 14 }}>{handleLabelDisplayedRows({ from: pagination.page * pagination.rowPerPage + 1, to: Math.min((pagination.page + 1) * pagination.rowPerPage, pagination.count), count: pagination.count })}</span>
            <ul className="pagination mb-0">
              <li className={`page-item ${pagination.page === 0 ? "disabled" : ""}`}>
                <button className="page-link" onClick={e => handlePageChange(e, pagination.page - 1)}>
                  &laquo;
                </button>
              </li>
              <li className={`page-item ${pagination.page >= totalPages - 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={e => handlePageChange(e, pagination.page + 1)}>
                  &raquo;
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <p
        className={clsx("regular-sm-txt")}
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          lineHeight: "20px",
          textAlign: "center",
          padding: "10px 0",
        }}
      >
        {getLabel(LangConstant.TXT_APP_COPYRIGHT)}
      </p>
      <CreateBranch open={isOpenCreate} onClose={handleCloseCreateBranchDialog} />
      <PopupConfirm
        open={Boolean(selectedBranch)}
        onClose={handleCloseConfirmDeleteBranchDialog}
        onAccept={handleDeleteBranch}
        title={getLabel(LangConstant.TXT_CONFIRM_DELETE_BRANCH)}
      />
    </div>
  );
};

export default BranchManagement;
